import { Box, Button, Grid, IconButton, ListSubheader, MenuItem, Stack, TextField, Typography, Alert, alpha, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Add, Delete, ScheduleRounded } from '@mui/icons-material';
import { ZoneAmountNumberField, ZoneSurgeMultiplierField } from './ZonePricingNumberField';
import { listOnDemandCategories } from '../../../services/businessService';
import { getMasters } from '../../../services/platform/masterService';
import { parseMasterListRows } from '../../../utils/masterRows';
import type { HubServiceOption, SurgeRow, ZoneFormState } from '../zoneTypes';
import {
  buildSurgeServiceGroups,
  formHasSelectedOnDemand,
  isOnDemandHubService,
  surgeServiceLabel,
} from '../zoneTypes';
const DAYS = [
  { v: 1, l: 'Mon' }, { v: 2, l: 'Tue' }, { v: 3, l: 'Wed' }, { v: 4, l: 'Thu' },
  { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' }, { v: 0, l: 'Sun' },
];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

/** Parse stored HH:mm (24h) into 12h parts. */
function parseHhMm(value: string): { hour12: number; minute: string; ampm: 'AM' | 'PM' } {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
  let h = m ? Number(m[1]) : 8;
  let minNum = m ? Number(m[2]) : 0;
  if (!Number.isFinite(h) || h < 0 || h > 23) h = 8;
  if (!Number.isFinite(minNum) || minNum < 0 || minNum > 59) minNum = 0;
  const min = String(minNum).padStart(2, '0');
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: min, ampm };
}

/** Build HH:mm (24h) from 12h UI parts. */
function toHhMm(hour12: number, minute: string, ampm: 'AM' | 'PM'): string {
  let h = hour12 % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${minute}`;
}

/** Display label e.g. 8:00 AM */
export function formatSurgeTime12h(value: string | number | undefined | null): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') {
    const h = Math.floor(value);
    const min = Math.round((value - h) * 60);
    return formatSurgeTime12h(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
  }
  const { hour12, minute, ampm } = parseHhMm(String(value));
  return `${hour12}:${minute} ${ampm}`;
}

function AmpmTimeField({
  label,
  value,
  onChange,
  error,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (hhmm: string) => void;
  error?: boolean;
  helperText?: string;
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const errorColor = theme.palette.error.main;
  const parts = parseHhMm(value);
  const [activeSeg, setActiveSeg] = useState<'hour' | 'minute' | 'ampm' | null>(null);
  const focused = activeSeg != null;
  const borderColor = error ? errorColor : focused ? accent : theme.palette.divider;

  const segSelectSx = (seg: 'hour' | 'minute') => ({
    minWidth: 0,
    flex: '0 0 auto',
    width: { xs: 52, sm: 56 },
    '& .MuiOutlinedInput-root': {
      bgcolor: activeSeg === seg ? alpha(accent, 0.1) : 'transparent',
      borderRadius: 1,
      fontWeight: 800,
      fontSize: { xs: 14, sm: 15 },
      color: activeSeg === seg ? accent : theme.palette.text.primary,
      transition: 'background-color .15s, color .15s',
      '& fieldset': { border: 'none' },
      '&:hover fieldset': { border: 'none' },
      '&.Mui-focused fieldset': { border: 'none' },
    },
    '& .MuiSelect-select': {
      py: 0.85,
      px: 0.5,
      textAlign: 'center',
      pr: '22px !important',
    },
    '& .MuiSelect-icon': {
      color: activeSeg === seg ? accent : theme.palette.text.secondary,
      right: 2,
    },
  });

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 220 } }}>
      <Typography
        variant="caption"
        sx={{
          mb: 0.6,
          display: 'block',
          fontWeight: 700,
          letterSpacing: 0.2,
          color: focused ? accent : 'text.secondary',
          transition: 'color .15s',
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 220 },
          border: '1.5px solid',
          borderColor,
          borderRadius: 2,
          bgcolor: error ? alpha(errorColor, 0.04) : focused ? alpha(accent, 0.04) : theme.palette.background.paper,
          overflow: 'hidden',
          boxShadow: error
            ? `0 0 0 3px ${alpha(errorColor, 0.14)}`
            : focused
              ? `0 0 0 3px ${alpha(accent, 0.14)}`
              : '0 1px 2px rgba(15,23,42,0.04)',
          transition: 'border-color .15s, box-shadow .15s, background-color .15s',
          '&:hover': {
            borderColor: error ? errorColor : focused ? accent : alpha(accent, 0.45),
          },
        }}
      >
        <TextField
          select
          size="small"
          value={parts.hour12}
          onOpen={() => setActiveSeg('hour')}
          onClose={() => setActiveSeg(null)}
          onFocus={() => setActiveSeg('hour')}
          onBlur={() => setActiveSeg((s) => (s === 'hour' ? null : s))}
          onChange={(e) => onChange(toHhMm(Number(e.target.value), parts.minute, parts.ampm))}
          sx={segSelectSx('hour')}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260 } } } }}
        >
          {HOURS_12.map((h) => (
            <MenuItem
              key={h}
              value={h}
              selected={parts.hour12 === h}
              sx={{
                fontWeight: parts.hour12 === h ? 800 : 500,
                color: parts.hour12 === h ? `${accent} !important` : 'text.primary',
                bgcolor: parts.hour12 === h ? alpha(accent, 0.1) : undefined,
                '&.Mui-selected': {
                  color: `${accent} !important`,
                  bgcolor: alpha(accent, 0.12),
                  fontWeight: 800,
                  '&:hover': { bgcolor: alpha(accent, 0.16) },
                },
              }}
            >
              {h}
            </MenuItem>
          ))}
        </TextField>

        <Typography
          fontWeight={800}
          sx={{
            px: 0.15,
            userSelect: 'none',
            color: focused ? accent : 'text.secondary',
            fontSize: 14,
            transition: 'color .15s',
          }}
        >
          :
        </Typography>

        <TextField
          select
          size="small"
          value={parts.minute}
          onOpen={() => setActiveSeg('minute')}
          onClose={() => setActiveSeg(null)}
          onFocus={() => setActiveSeg('minute')}
          onBlur={() => setActiveSeg((s) => (s === 'minute' ? null : s))}
          onChange={(e) => onChange(toHhMm(parts.hour12, e.target.value, parts.ampm))}
          sx={segSelectSx('minute')}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260 } } } }}
        >
          {MINUTES.map((m) => (
            <MenuItem
              key={m}
              value={m}
              selected={parts.minute === m}
              sx={{
                fontWeight: parts.minute === m ? 800 : 500,
                color: parts.minute === m ? `${accent} !important` : 'text.primary',
                bgcolor: parts.minute === m ? alpha(accent, 0.1) : undefined,
                '&.Mui-selected': {
                  color: `${accent} !important`,
                  bgcolor: alpha(accent, 0.12),
                  fontWeight: 800,
                  '&:hover': { bgcolor: alpha(accent, 0.16) },
                },
              }}
            >
              {m}
            </MenuItem>
          ))}
        </TextField>

        <Box
          onMouseDown={() => setActiveSeg('ampm')}
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            p: 0.4,
            gap: 0.35,
            bgcolor: alpha(accent, 0.08),
            borderLeft: '1px solid',
            borderColor: focused ? alpha(accent, 0.35) : 'divider',
            alignSelf: 'stretch',
            minWidth: { xs: 78, sm: 86 },
          }}
        >
          {(['AM', 'PM'] as const).map((period) => {
            const active = parts.ampm === period;
            return (
              <Box
                key={period}
                component="button"
                type="button"
                onFocus={() => setActiveSeg('ampm')}
                onBlur={() => setActiveSeg((s) => (s === 'ampm' ? null : s))}
                onClick={() => {
                  setActiveSeg('ampm');
                  onChange(toHhMm(parts.hour12, parts.minute, period));
                }}
                sx={{
                  flex: 1,
                  height: 28,
                  border: 0,
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontWeight: 800,
                  fontSize: 11,
                  letterSpacing: 0.4,
                  fontFamily: 'inherit',
                  lineHeight: 1,
                  bgcolor: active ? accent : 'transparent',
                  color: active ? '#ffffff' : accent,
                  transition: 'background-color .15s, color .15s',
                  '&:hover': {
                    bgcolor: active ? theme.palette.primary.dark : alpha(accent, 0.12),
                    color: active ? '#ffffff' : accent,
                  },
                }}
              >
                {period}
              </Box>
            );
          })}
        </Box>
      </Stack>
      {(helperText || error) && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block', lineHeight: 1.35 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

interface Props {
  form: ZoneFormState;
  setForm: (f: ZoneFormState) => void;
  allServices: HubServiceOption[];
  currency: string;
  showErrors?: boolean;
  nameErrors?: Record<number, string>;
  timeErrors?: Record<number, string>;
}

type OnDemandCategoryOption = { code: string; name: string; parentName?: string };
const defaultSurgeName = (existing: SurgeRow[]): string => {
  const used = new Set(existing.map((r) => r.name.trim().toLowerCase()));
  const candidates = ['Peak surge', 'Evening surge', 'Weekend surge', 'Night surge'];
  const available = candidates.find((c) => !used.has(c.toLowerCase()));
  if (available) return available;
  let n = existing.length + 1;
  while (used.has(`surge rule ${n}`)) n += 1;
  return `Surge rule ${n}`;
};

const emptySurge = (existing: SurgeRow[]): SurgeRow => ({
  name: defaultSurgeName(existing),
  multiplier: 1.3,
  surgePerKm: 0,
  startTime: '08:00',
  endTime: '11:00',
  daysOfWeek: [1, 2, 3, 4, 5],
});

export default function ZoneSurgeStep({ form, setForm, allServices, currency, showErrors, nameErrors = {}, timeErrors = {} }: Props) {
  const rows = form.surgePricing;
  const serviceGroups = useMemo(
    () => buildSurgeServiceGroups(form, allServices),
    [form.selectedServiceIds, allServices],
  );
  const hasOnDemand = formHasSelectedOnDemand(form, allServices);

  const { data: onDemandCategories = [] } = useQuery({
    queryKey: ['on-demand-categories', 'zone-surge'],
    queryFn: async (): Promise<OnDemandCategoryOption[]> => {
      try {
        const fromApi = await listOnDemandCategories();
        if (fromApi?.length) {
          const byCode = new Map(fromApi.map((c) => [c.code, c]));
          return fromApi
            .map((c) => ({
              code: c.code,
              name: c.name,
              parentName: c.parentCode ? byCode.get(c.parentCode)?.name : undefined,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        }
      } catch {
        /* fall through to masters */
      }
      const res = await getMasters('service_categories', { limit: 500 });
      const masterRows = parseMasterListRows<{
        _id: string;
        code: string;
        name: string;
        parentId?: string;
        metadata?: Record<string, unknown>;
      }>(res);
      const byId = new Map(masterRows.map((r) => [r._id, r]));
      const byCode = new Map(masterRows.map((r) => [r.code, r]));
      return masterRows
        .filter((r) => !r.metadata?.module || r.metadata.module === 'on_demand')
        .map((r) => {
          const parentCode = (r.metadata?.parentCode as string | undefined)
            || (r.parentId ? byId.get(r.parentId)?.code : undefined);
          return {
            code: r.code,
            name: r.name,
            parentName: parentCode ? byCode.get(parentCode)?.name : undefined,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: hasOnDemand,
    staleTime: 60_000,
  });

  const updateRow = (idx: number, patch: Partial<SurgeRow>) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, surgePricing: next });
  };

  const onServiceChange = (idx: number, serviceId: string) => {
    const nextId = serviceId || undefined;
    const patch: Partial<SurgeRow> = { serviceId: nextId };
    if (!isOnDemandHubService(nextId, allServices)) {
      patch.serviceCategoryCode = undefined;
    }
    updateRow(idx, patch);
  };

  const toggleDay = (idx: number, day: number) => {
    const row = rows[idx];
    const days = row.daysOfWeek.includes(day)
      ? row.daysOfWeek.filter((d) => d !== day)
      : [...row.daysOfWeek, day];
    updateRow(idx, { daysOfWeek: days.sort() });
  };

  const hasDuplicateNameError = showErrors
    && Object.values(nameErrors).some((msg) => msg.includes('already used'));

  return (
    <Box>
      {hasDuplicateNameError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Each surge rule must have a unique name. Rename or remove duplicate rules before continuing.
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
            <ScheduleRounded color="warning" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={800}>Surge pricing</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Peak windows in 12-hour time (AM / PM)
          </Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setForm({ ...form, surgePricing: [...rows, emptySurge(rows)] })}>
          Add rule
        </Button>
      </Stack>

      {!rows.length && (
        <Typography variant="body2" color="text.secondary">No surge rules — flat pricing applies.</Typography>
      )}

      {rows.map((row, idx) => (
        <Box
          key={idx}
          sx={{
            mb: 2,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2.5,
            bgcolor: '#fff',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>Rule {idx + 1}</Typography>
            <IconButton size="small" color="error" onClick={() => setForm({ ...form, surgePricing: rows.filter((_, i) => i !== idx) })}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Rule name"
                value={row.name}
                required
                onChange={(e) => updateRow(idx, { name: e.target.value })}
                error={Boolean(showErrors && nameErrors[idx])}
                helperText={
                  showErrors
                    ? (nameErrors[idx] || 'Required · unique name for each rule')
                    : 'Required · unique name for each rule'
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Service (optional)"
                value={row.serviceId || ''}
                onChange={(e) => onServiceChange(idx, e.target.value)}
                SelectProps={{
                  MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
                }}
                helperText="Choose hub first — Transport, Delivery, or On Demand"
              >
                <MenuItem value="">All services</MenuItem>
                {serviceGroups.map((group) => [
                  <ListSubheader key={`${group.hub}-header`} sx={{ fontWeight: 800, lineHeight: '32px' }}>
                    {group.label}
                  </ListSubheader>,
                  ...group.services.map((svc) => (
                    <MenuItem key={svc._id} value={svc._id} sx={{ pl: 3 }}>
                      {surgeServiceLabel(svc)}
                    </MenuItem>
                  )),
                ])}
              </TextField>
            </Grid>
            {isOnDemandHubService(row.serviceId, allServices) && (
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="On-demand service"
                  value={row.serviceCategoryCode || ''}
                  onChange={(e) => updateRow(idx, { serviceCategoryCode: e.target.value || undefined })}
                  helperText={
                    onDemandCategories.length
                      ? 'Optional — plumber, electrician, etc.'
                      : 'Loading on-demand catalog…'
                  }
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
                  }}
                >
                  <MenuItem value="">All on-demand services</MenuItem>
                  {onDemandCategories.map((cat) => (
                    <MenuItem key={cat.code} value={cat.code}>
                      {cat.parentName ? `${cat.parentName} · ${cat.name}` : cat.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6} sm={2}>
              <ZoneSurgeMultiplierField
                label="Multiplier"
                value={row.multiplier}
                onValueChange={(n) => updateRow(idx, { multiplier: n })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <ZoneAmountNumberField
                label="Surge per km"
                currency={currency}
                value={row.surgePerKm ?? 0}
                onValueChange={(n) => updateRow(idx, { surgePerKm: n })}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <AmpmTimeField
                label="Start time"
                value={row.startTime}
                onChange={(startTime) => updateRow(idx, { startTime })}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <AmpmTimeField
                label="End time"
                value={row.endTime}
                onChange={(endTime) => updateRow(idx, { endTime })}
                error={Boolean(showErrors && timeErrors[idx])}
                helperText={
                  showErrors && timeErrors[idx]
                    ? timeErrors[idx]
                    : 'Must be later than start time (same day)'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.75, display: 'block' }}>
                Active days
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {DAYS.map((d) => (
                  <Button
                    key={d.v}
                    size="small"
                    variant={row.daysOfWeek.includes(d.v) ? 'contained' : 'outlined'}
                    onClick={() => toggleDay(idx, d.v)}
                    sx={{ minWidth: 48, borderRadius: '10px', fontWeight: 700 }}
                  >
                    {d.l}
                  </Button>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
